/**
 * User Settings Hooks
 * React Query hooks for user settings management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUserSettings,
  updateUserSettings,
  uploadAvatar,
  deleteAvatar,
  updateProfile,
} from '@/services/user-settings.service';
import type { PartialUserSettings } from '@/services/user-settings.service';
import { useAuthStore } from '@/store/auth.store';
import type { User } from '@/types/api.types';

/**
 * Get user settings
 * Automatically disabled when user is not authenticated
 */
export const useUserSettings = (options?: { enabled?: boolean }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery({
    queryKey: ['userSettings'],
    queryFn: getUserSettings,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled !== undefined ? options.enabled : isAuthenticated,
  });
};

/**
 * Update user settings
 */
export const useUpdateSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: PartialUserSettings) => updateUserSettings(updates),
    onSuccess: (data) => {
      // Update cached settings
      queryClient.setQueryData(['userSettings'], data);

      // Invalidate to refetch if needed
      queryClient.invalidateQueries({ queryKey: ['userSettings'] });
    },
  });
};

/**
 * Upload avatar
 */
export const useUploadAvatar = () => {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: (file: File) => uploadAvatar(file),
    onSuccess: (user) => {
      // Update Zustand auth store (persisted to localStorage)
      if (currentUser) {
        setUser({
          ...currentUser,
          avatar_url: user.avatar_url,
        } as User);
      }

      // Update React Query cache
      queryClient.setQueryData<User>(['user'], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          avatar_url: user.avatar_url,
        };
      });

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

/**
 * Delete avatar
 */
export const useDeleteAvatar = () => {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: deleteAvatar,
    onSuccess: () => {
      // Update Zustand auth store (persisted to localStorage)
      if (currentUser) {
        setUser({
          ...currentUser,
          avatar_url: null,
        } as User);
      }

      // Update React Query cache
      queryClient.setQueryData<User>(['user'], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          avatar_url: null,
        };
      });

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

/**
 * Update profile
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: (updates: {
      display_name?: string;
    }) => updateProfile(updates),
    onSuccess: (user) => {
      // Update Zustand auth store (persisted to localStorage)
      if (currentUser) {
        setUser({
          ...currentUser,
          display_name: user.display_name,
        } as User);
      }

      // Update React Query cache
      queryClient.setQueryData<User>(['user'], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          display_name: user.display_name,
        };
      });

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};
